import {ThemeProvider} from "@vebox/etui-system";
import * as React from "react";
import {Display} from "../../components/Display";
import {GranularityType, Toolbar} from "../../components/toolsbar/Toolbar";
import {AppTranslations} from "../../components/Utils/translations/AppTranslations";
import {operativityColors, themeDefaultValues, veboxDefaultTheme} from "../../index";
import {DataPointWithID} from "./charts/common";
import {Histogram} from "./charts/Histogram";
import {HistogramEventsHandler} from "./charts/HistogramEventsHandler";
import {LineChart} from "./charts/LineChart";
import {Header} from "./components/Header";
import {LoadingOverlay} from "./components/LoadingOverlay";
import {Tooltip, TooltipCoordinate} from "./components/Tooltip";
import {BubbleInfoDisplay} from "./components/TooltipBubble";
import {Viewport} from "./components/Viewport";
import "./operativity.scss";
import {generateFilter, getOperativity, getReport, getTotal} from "./operativity_service";
import {operativityTime, unitFromGranularity} from "./shared";
import AutoSizer from "react-virtualized-auto-sizer";
import d3 = require("d3");
import moment = require("moment");

enum ChartType {
    BAR,
    LINE,
}

interface Props {
    data: any;
}

interface State {
    response?: any;
    viewData?: DataPointWithID[];
    data?: DataPointWithID[];
    dataID?: string;
    format?: ChartType;
    pos?: TooltipCoordinate;
    showInfo?: boolean;
    showBrush?: boolean;
    selectionStartDate?: Date;
    selectionEndDate?: Date;
    timeExtentStartDate?: Date;
    timeExtentEndDate?: Date;
    granularity: GranularityType;
    loading: boolean;
    activeEquipment: number;
    totalEquipment: number;
    filterGuid?: string;
    loadingExcel?: boolean;
    isDynamicChartScale: boolean;
}

/**
 * Some parameters (startingTimes, colors) are found on the ./shared.ts file
 */

declare var AppTranslations: AppTranslations;

export class Operativity extends React.Component<Props, State> {

    private toolbarRef: React.RefObject<Toolbar>;

    private toolbarTranslations = Object.freeze({
        unit_measure_litres: AppTranslations.operativityPanel.unitMeasureLitres,
        granularity_yearly_unit: AppTranslations.operativityPanel.granularityYearlyUnit,
        tool_info_cursor: AppTranslations.operativityPanel.toolInfoCursor,
        sales_weekly: AppTranslations.operativityPanel.salesWeekly,
        granularity_monthly: AppTranslations.operativityPanel.granularityMonthly,
        tool_export: AppTranslations.operativityPanel.toolExport,
        no_sales_for_location: AppTranslations.operativityPanel.noSalesForLocation,
        granularity_daily: AppTranslations.operativityPanel.granularityDaily,
        granularity_daily_unit: AppTranslations.operativityPanel.granularityDailyUnit,
        product_chart_style_label: AppTranslations.operativityPanel.productChartStyleLabel,
        granularity_hourly_unit: AppTranslations.operativityPanel.granularityHourlyUnit,
        unit_measure_litres_short: AppTranslations.operativityPanel.unitMeasureLitresShort,
        total: AppTranslations.operativityPanel.total,
        tool_pan_and_zoom: AppTranslations.operativityPanel.toolPanAndZoom,
        sales_hourly: AppTranslations.operativityPanel.salesHourly,
        unit_measure_kilograms_short: AppTranslations.operativityPanel.unitMeasureKilogramsShort,
        granularity_weekly_unit: AppTranslations.operativityPanel.granularityWeeklyUnit,
        sales_daily: AppTranslations.operativityPanel.salesDaily,
        no_sales_for_equipment: AppTranslations.operativityPanel.noSalesForEquipment,
        sales_yearly: AppTranslations.operativityPanel.salesYearly,
        entity_equipment: AppTranslations.operativityPanel.entityEquipment,
        sales_quarterly: AppTranslations.operativityPanel.salesQuarterly,
        granularity_weekly: AppTranslations.operativityPanel.granularityWeekly,
        entity_brand: AppTranslations.operativityPanel.entityBrand,
        unit_measure_units_short: AppTranslations.operativityPanel.unitMeasureUnitsShort,
        granularity_quarterly: AppTranslations.operativityPanel.granularityQuarterly,
        unit_measure_units: AppTranslations.operativityPanel.unitMeasureUnits,
        entity_location: AppTranslations.operativityPanel.entityLocation,
        unit_measure_shoots_short: AppTranslations.operativityPanel.unitMeasureShootsShort,
        unit_measure_shoots: AppTranslations.operativityPanel.unitMeasureShoots,
        sales_monthly: AppTranslations.operativityPanel.salesMonthly,
        granularity_hourly: AppTranslations.operativityPanel.granularityHourly,
        granularity_quarterly_unit: AppTranslations.operativityPanel.granularityQuarterlyUnit,
        no_sales: AppTranslations.operativityPanel.noSales,
        granularity_yearly: AppTranslations.operativityPanel.granularityYearly,
        granularity_monthly_unit: AppTranslations.operativityPanel.granularityMonthlyUnit,
    });

    constructor(props) {
        super(props);

        this.toolbarRef = React.createRef();

        this.state = {
            data: [],
            viewData: [],
            format: ChartType.BAR,
            showInfo: false,
            showBrush: false,
            selectionStartDate: operativityTime.startingSelectedExtent.start,
            selectionEndDate: operativityTime.startingSelectedExtent.end,
            timeExtentStartDate: operativityTime.startingTimeExtent.start,
            timeExtentEndDate: operativityTime.startingTimeExtent.end,
            granularity: GranularityType.DAY, // Default
            loading: true,
            totalEquipment: 1,
            activeEquipment: 0,
            isDynamicChartScale: false
        };
    }

    public render() {
        const barColor = () => {
            return operativityColors.bars;
        };

        const selectedBarColor = (d: DataPointWithID) => {
            return this.state.dataID === d.id ? operativityColors.bars_hover : operativityColors.bars
        };

        const value = this.state.data.find((x) => x.id === this.state.dataID);

        const headerEndDate = this.state.granularity !== GranularityType.HOUR
            ? moment(this.state.selectionEndDate.getTime()).add(-1, "days").toDate()
            : this.state.selectionEndDate;

        return (
            <ThemeProvider theme={veboxDefaultTheme}>
                <div className={"operativity-panel"}>
                    <Header
                        className={"header"}
                        granularity={this.state.granularity}
                        startDate={this.state.selectionStartDate}
                        endDate={headerEndDate}
                        value={this.state.activeEquipment}
                        total={this.state.totalEquipment}
                        onDynamicToggle={this.changeScaleMode}
                    />
                    <div className={"main"}>
                        <AutoSizer style={{width: "100%", height: "100%"}}>
                            {({width, height}) =>
                                <Display isVisible={this.state.format === ChartType.BAR}>
                                    <Tooltip
                                        bubbleContent={
                                            value ?
                                                <BubbleInfoDisplay
                                                    title={
                                                        (window as any).getSalesXAxisTimeFormatter(
                                                            this.state.granularity,
                                                            new Date(value.id),
                                                            0,
                                                            d3.timeFormatLocale((window as any)
                                                                .getVBXChartLocaleFormatter()).format,
                                                            true,
                                                        )
                                                    }
                                                    percentageValue={(value.value / this.state.totalEquipment) * 100.0}
                                                    value={value.value}
                                                    total={this.state.totalEquipment}
                                                /> : null
                                        }
                                        bubbleStyle={{
                                            backgroundColor: themeDefaultValues.tooltipBg,
                                            borderRadius: themeDefaultValues.tooltipBorderRadius,
                                        }}
                                        pos={this.state.pos}
                                        boundary={{
                                            top: 0,
                                            left: 0,
                                            width: width,
                                            height: height,
                                        }}
                                    >
                                        <HistogramEventsHandler
                                            onHoverColor={operativityColors.bars_hover}
                                            onHover={this.onCursorOnBar}
                                            onHoverEnabled={this.state.showInfo}
                                            onBrush={this.onChartBrushEnd}
                                            onBrushEnabled={this.state.showBrush}
                                            withSize={{width, height}}
                                        >
                                            <Histogram
                                                containerClassName={"histogram-container"}
                                                barsClassName={"histogram-bars"}
                                                labelClassName={"histogram-labels"}
                                                data={this.state.viewData}
                                                barFillColor={selectedBarColor}
                                                withSize={{width, height}}
                                                unitOfMeasure={"%"}
                                                maxValue={this.state.isDynamicChartScale ? undefined : 100}
                                            />
                                        </HistogramEventsHandler>
                                    </Tooltip>
                                </Display>}
                        </AutoSizer>
                        <Display isVisible={this.state.format === ChartType.LINE}>
                            <LineChart
                                containerClassName={"histogram-container"}
                                labelClassName={"histogram-labels"}
                                data={this.state.viewData}
                                barFillColor={barColor}
                            />
                        </Display>
                    </div>
                    <Viewport
                        toolbarRef={this.toolbarRef}
                        translations={this.toolbarTranslations}
                        onPanSelected={this.onPanSelected}
                        onInfoSelected={this.onInfoSelected}
                        onExport={this.onExport}
                        onBrushEnd={this.onTimelineBrushEnd}
                        onTimeExtentChange={this.onTimeExtentChange}
                        enabled={true}
                        timelineSelectedPeriod={{
                            start: moment(this.state.selectionStartDate),
                            end: moment(this.state.selectionEndDate),
                        }}
                        timelineExtent={{
                            start: moment(this.state.timeExtentStartDate),
                            end: moment(this.state.timeExtentEndDate),
                        }}
                        timelineMaxExtent={operativityTime.maxExtent}
                        loadingExcel={this.state.loadingExcel}
                        height={75}
                    />
                    <Display isVisible={this.state.loading}>
                        <LoadingOverlay/>
                    </Display>
                </div>
            </ThemeProvider>
        );
    }

    public open = (newFilter: any) => {
        const $main = $(".main");
        const $brush = $main.find(".brush");
        const $overlay = $main.find(".overlay");
        if ($brush) {
            $brush?.attr("pointer-events", "all");
            $overlay?.attr("pointer-events", "all");
        }

        this.setState({
            showBrush: true,
        }, () => this.update(newFilter));
    }

    public update = (newFilter) => {
        this.setState({loading: true}, () => {
            generateFilter(newFilter).then((filterGuid) => {
                this.setState({
                    filterGuid,
                }, () => {
                    this.loadData(this.state.timeExtentStartDate, this.state.timeExtentEndDate);
                });
            });
        });
    }

    public hide = () => {
        const $main = $(".main");
        const $brush = $main.find(".brush");
        const $overlay = $main.find(".overlay");
        if ($brush) {
            $brush.attr("pointer-events", "none");
            $overlay.attr("pointer-events", "none");
        }

        this.setState({
            showBrush: false,
        });
    }

    public close = () => {
        this.hide();
    }

    private onPanSelected = () => {
        this.setState((state) => ({
            ...state,
            showBrush: true,
            showInfo: false,
        }));
    }

    private onInfoSelected = () => {
        this.setState((state) => ({
            ...state,
            showBrush: false,
            showInfo: true,
        }));
    }

    private onExport = () => {
        this.setState({
            loadingExcel: true,
        });
        getReport(
            this.state.filterGuid,
            this.state.selectionStartDate,
            this.state.selectionEndDate,
            this.state.granularity,
        )
            .then(() => {
                console.log("Downloading...");
                this.setState({
                    loadingExcel: false,
                });
            })
            .catch(console.error).then(() => {
            this.setState({
                loadingExcel: false,
            });
        });
    }

    private onTimeExtentChange = (granularity: GranularityType) => {
        this.setState((state) => {
            const start = this.toolbarRef.current.state.timelineSelectedPeriod.start.toDate();
            const end = this.toolbarRef.current.state.timelineSelectedPeriod.end.toDate();

            const extentStart = this.toolbarRef.current.state.timelineExtent.start.toDate();
            const extentEnd = this.toolbarRef.current.state.timelineExtent.end.toDate();

            return {
                selectionStartDate: start,
                selectionEndDate: end,
                timeExtentStartDate: extentStart,
                timeExtentEndDate: extentEnd,
                granularity,
                loading: (granularity && (state.granularity !== granularity))
                    || (extentStart !== state.timeExtentStartDate)
                    || (state.timeExtentEndDate !== extentEnd),
            };
        }, () => {
            if (this.state.loading) {
                this.loadData(
                    this.state.timeExtentStartDate,
                    this.state.timeExtentEndDate,
                );
            } else {
                this.updateTotals(this.state.selectionStartDate, this.state.selectionEndDate);
            }
        });
    }

    private onCursorOnBar = (data: DataPointWithID, e) => {

        if (data) {
            const rectWidth = Number(e.target.getAttribute("width"));
            const rectX = Number(e.target.getAttribute("x"));
            const rectY = Number(e.target.getAttribute("y"));

            this.setState({
                dataID: data.id as string,
                pos: {
                    left: rectX + rectWidth / 2,
                    top: rectY,
                },
            });
        } else {
            this.setState({
                dataID: null,
                pos: null,
            });
        }
        e.stopPropagation();
    }

    private onChartBrushEnd = ([start, end]: number[]) => {
        this.setState((state) => {
            const newViewData = state.viewData.slice(
                Math.floor(start * state.viewData.length),
                Math.ceil(end * state.viewData.length),
            ).map((x, i, array) => ({
                ...x,
                label: (window as any).getSalesXAxisTimeFormatter(
                    this.state.granularity,
                    new Date(x.id),
                    i,
                    d3.timeFormatLocale((window as any).getVBXChartLocaleFormatter()).format,
                    false,
                    array.length,
                ),
            }));

            return {
                ...state,
                viewData: newViewData.length < 1 ? state.viewData : newViewData,
            };
        }, () => {
            const from = moment(new Date(this.state.viewData[0].id));
            const to = moment(this.state.viewData.slice(-1)[0].id)
                .add(1, unitFromGranularity(this.state.granularity) as any);

            this.setState({
                selectionStartDate: from.toDate(),
                selectionEndDate: to.toDate(),
            }, () => {
                (this.toolbarRef.current as any).handleTimelineSelectedPeriodChange({
                    start: from,
                    end: to,
                });
                this.updateTotals(this.state.selectionStartDate, this.state.selectionEndDate);
            });
        });
    }

    private onTimelineBrushEnd = ([start, end]: Date[]) => {
        const from = start.getTime();
        const to = end.getTime();

        if (this.state.selectionStartDate.getTime() === start.getTime()
            && this.state.selectionEndDate.getTime() === end.getTime()) {
            return;
        }

        this.setState((state) => {

            const newViewData = state.data.filter((x) => from <= +x.id && +x.id < to).map((x, i, array) => ({
                ...x,
                value: (x.value * 1.0 / this.state.totalEquipment) * 100.0,
                label: (window as any).getSalesXAxisTimeFormatter(
                    this.state.granularity,
                    new Date(x.id),
                    i,
                    d3.timeFormatLocale((window as any).getVBXChartLocaleFormatter()).format,
                    false,
                    array.length,
                ),
            }));

            return {
                viewData: newViewData,
                selectionStartDate: start,
                selectionEndDate: end,
            };
        }, () => {

            const granularity = this.toolbarRef.current.state.granularitySelected;

            if (granularity && (this.state.granularity !== granularity)) {
                this.setState({
                    loading: true,
                    granularity,
                    timeExtentStartDate: this.toolbarRef.current.state.timelineExtent.start.toDate(),
                    timeExtentEndDate: this.toolbarRef.current.state.timelineExtent.end.toDate(),
                }, () => {
                    this.loadData(
                        this.state.timeExtentStartDate,
                        this.state.timeExtentEndDate,
                    );
                });
            } else {
                if (this.toolbarRef.current.state.hasBrushEnded) {
                    this.updateTotals(this.state.selectionStartDate, this.state.selectionEndDate);
                }
            }
        });
    }

    private loadData = (start: Date, end: Date) => {
        Promise.all([
                getOperativity(
                    this.state.filterGuid,
                    this.state.timeExtentStartDate,
                    this.state.timeExtentEndDate,
                    this.state.granularity,
                ),
                getTotal(
                    this.state.filterGuid,
                    this.state.selectionStartDate,
                    this.state.selectionEndDate,
                ),
            ],
        ).then(([result, totals]) => {
            // Convert data
            const data = result.operativities.map<DataPointWithID>((x, i, array) => {
                const date = new Date(x.date);
                return {
                    id: date.getTime(),
                    value: x.value,
                    label: (window as any).getSalesXAxisTimeFormatter(
                        this.state.granularity,
                        date,
                        i,
                        d3.timeFormatLocale((window as any).getVBXChartLocaleFormatter()).format,
                        false,
                        array.length,
                    ),
                };
            });

            const from = this.state.selectionStartDate.getTime();
            const to = this.state.selectionEndDate.getTime();

            const visibleData = data
                .filter((x) => from <= +x.id && +x.id < to)
                .map((x, i, array) => ({
                    ...x,
                    value: (x.value * 1.0 / totals.totalEquipments) * 100.0,
                    label: (window as any).getSalesXAxisTimeFormatter(
                        this.state.granularity,
                        new Date(x.id),
                        i,
                        d3.timeFormatLocale((window as any).getVBXChartLocaleFormatter()).format,
                        false,
                        array.length,
                    ),
                }));

            this.setState({
                data,
                viewData: visibleData,
                loading: false,
                totalEquipment: totals.totalEquipments,
                activeEquipment: totals.operativeEquipment,
            });
        })
            .catch(console.error);
    }

    private updateTotals = (start: Date, end: Date) => {
        getTotal(this.state.filterGuid, start, end)
            .then((totals) => {
                this.setState({
                    totalEquipment: totals.totalEquipments,
                    activeEquipment: totals.operativeEquipment,
                });
            });
    }

    private changeScaleMode = (isChecked) => {
        this.setState({
            isDynamicChartScale: isChecked,
        });
    }
}
